import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { orgApi, type Org } from "../api/orgApi";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Fade from "@mui/material/Fade";
import Grow from "@mui/material/Grow";
import CustomButton from "../components/CustomButton";

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export function OrganizationsPage() {
  const { saveBootstrapToken, hasBootstrapToken } = useAuth();
  const nav = useNavigate();

  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // token entry
  const [tokenInput, setTokenInput] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);

  // create org form
  const [showCreate, setShowCreate] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [orgDesc, setOrgDesc] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      setOrgs(await orgApi.listOrgs());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (hasBootstrapToken) load();
  }, [hasBootstrapToken]);

  async function onSaveToken(e: FormEvent) {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    setTokenBusy(true);
    setErr(null);
    try {
      saveBootstrapToken(tokenInput.trim());
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setTokenBusy(false);
    }
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!orgName.trim() || !orgSlug.trim()) return;
    setCreateBusy(true);
    setErr(null);
    try {
      const org = await orgApi.createOrg(orgName.trim(), orgSlug.trim(), orgDesc.trim() || undefined);
      setOrgs((prev) => [org, ...prev]);
      setOrgName("");
      setOrgSlug("");
      setOrgDesc("");
      setShowCreate(false);
      nav(`/organizations/${org.id}`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCreateBusy(false);
    }
  }

  // Token entry view (no bootstrap token saved yet)
  if (!hasBootstrapToken) {
    return (
      <Box className="login-page">
        <Card className="login-card">
          <CardContent component="form" onSubmit={onSaveToken} className="login-card-content">
            <Typography variant="h6" className="login-title">
              Admin access
            </Typography>
            <Typography variant="body2" className="login-subtitle">
              Enter your bootstrap token to manage organizations and projects.
            </Typography>

            <Box className="login-fields">
              <TextField
                label="Bootstrap token"
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="admin token from .env"
                size="small"
                fullWidth
                autoFocus
                className="login-input"
              />
              <CustomButton
                loading={tokenBusy}
                buttonText="Continue"
                buttonTextLoading="Verifying…"
                handleSubmitClick={() => {}}
              />
            </Box>

            {err && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {err}
              </Alert>
            )}
          </CardContent>
        </Card>

        <Typography variant="body2" className="login-subtitle" sx={{ mt: 1 }}>
          Already have an API key?{" "}
          <Link to="/login" className="login-link">Sign in to a project</Link>
        </Typography>
      </Box>
    );
  }

  // Main organizations list view
  return (
    <Box className="orgs-page">
      {/* Header row */}
      <Box className="orgs-header">
        <Box>
          <Typography variant="h5" className="orgs-page-title">
            Organizations
          </Typography>
          <Typography variant="body2" className="login-subtitle">
            Manage orgs and their projects.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {!showCreate && <CustomButton
          loading={false}
          buttonText="+ New organization"
          buttonTextLoading=""
          handleSubmitClick={() => setShowCreate((v) => !v)}
        />}
      </Box>

      {/* Create org form */}
      {showCreate && (
        <Card className="orgs-create-card" sx={{ mb: 3 }}>
          <CardContent component="form" onSubmit={onCreate} className="login-card-content">
            <Typography variant="h6" className="login-title">
              Create organization
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Name"
                  value={orgName}
                  onChange={(e) => { setOrgName(e.target.value); setOrgSlug(slugify(e.target.value)); }}
                  placeholder="Acme Corp"
                  size="small"
                  fullWidth
                  autoFocus
                  className="login-input"
                />
                <TextField
                  label="Slug (URL-safe)"
                  value={orgSlug}
                  onChange={(e) => setOrgSlug(slugify(e.target.value))}
                  placeholder="acme-corp"
                  size="small"
                  fullWidth
                  className="login-input"
                />
              </Box>
              <TextField
                label="Description (optional)"
                value={orgDesc}
                onChange={(e) => setOrgDesc(e.target.value)}
                placeholder="Short description"
                size="small"
                fullWidth
                className="login-input"
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
              <CustomButton
                loading={false}
                buttonText="Cancel"
                buttonTextLoading=""
                handleSubmitClick={() => setShowCreate(false)}
                background="transparent"
                color="rgba(74, 30, 71, 1)"
                border="1px solid rgba(74, 30, 71, 0.70)"
                hoverBgColor="rgba(74, 30, 71, 0.08)"
              />
              <CustomButton
                loading={createBusy}
                disabled={!orgName.trim() || !orgSlug.trim()}
                buttonText="Create organization"
                buttonTextLoading="Creating…"
                handleSubmitClick={() => {}}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {/* Orgs list */}
      {loading ? (
        <Fade in timeout={300}>
          <Typography variant="body2" className="login-subtitle">Loading…</Typography>
        </Fade>
      ) : orgs.length === 0 ? (
        <Fade in timeout={500}>
          <Card className="login-card" sx={{ textAlign: "center", py: 6, maxWidth: "none" }}>
            <CardContent>
              <Box className="empty-state">
                <Box className="empty-state-icon">✦</Box>
                <Typography className="empty-state-title">No organizations yet</Typography>
                <Typography className="empty-state-desc">Create your first organization to start managing projects.</Typography>
                <CustomButton
                  loading={false}
                  buttonText="Create your first organization"
                  buttonTextLoading=""
                  handleSubmitClick={() => setShowCreate(true)}
                />
              </Box>
            </CardContent>
          </Card>
        </Fade>
      ) : (
        <Box>
          {/* View toggle */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 2 }}>
            <Box className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
              >
                ▦ Grid
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                ≡ List
              </button>
            </Box>
          </Box>

          {viewMode === "grid" ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 2 }}>
              {orgs.map((org, index) => {
                const initials = org.name
                  .split(/[\s_-]+/)
                  .map((w) => w[0]?.toUpperCase() ?? "")
                  .slice(0, 2)
                  .join("");
                const d = new Date(org.created_at);
                const day = d.getDate();
                const suffix = [11,12,13].includes(day) ? "th" : ({1:"st",2:"nd",3:"rd"} as Record<number,string>)[day % 10] || "th";
                const month = d.toLocaleString(undefined, { month: "short" });
                const year = d.getFullYear();
                let hours = d.getHours();
                const mins = d.getMinutes().toString().padStart(2, "0");
                const ampm = hours >= 12 ? "pm" : "am";
                hours = hours % 12 || 12;
                const dateStr = `${day}${suffix} ${month} ${year}, ${hours}:${mins} ${ampm}`;

                return (
                  <Grow in timeout={400 + index * 80} key={org.id}>
                    <Link to={`/organizations/${org.id}`} style={{ textDecoration: "none" }}>
                      <Card className="project-grid-card">
                        <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1, p: 2.5, "&:last-child": { pb: 2.5 } }}>
                          <Box className="project-grid-avatar">
                            {initials}
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "var(--brand-black)", mt: 1 }}>
                            {org.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "var(--brand-muted)" }}>
                            {org.description || org.slug}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(147,147,153,1)", fontSize: "11px" }}>
                            {dateStr}
                          </Typography>
                          <Typography sx={{ color: "var(--brand-muted)", mt: 1, fontSize: "18px" }}><img src="/arrow_right.png" alt="Go" style={{ width: 18, height: 18 }} /></Typography>
                        </CardContent>
                      </Card>
                    </Link>
                  </Grow>
                );
              })}
            </Box>
          ) : (
            <Card className="orgs-create-card">
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Slug</th>
                      <th>Description</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map((org) => {
                      const d = new Date(org.created_at);
                      const day = d.getDate();
                      const suffix = [11,12,13].includes(day) ? "th" : ({1:"st",2:"nd",3:"rd"} as Record<number,string>)[day % 10] || "th";
                      const month = d.toLocaleString(undefined, { month: "short" });
                      const year = d.getFullYear();
                      let hours = d.getHours();
                      const mins = d.getMinutes().toString().padStart(2, "0");
                      const ampm = hours >= 12 ? "pm" : "am";
                      hours = hours % 12 || 12;
                      const dateStr = `${day}${suffix} ${month} ${year}, ${hours}:${mins} ${ampm}`;

                      return (
                        <tr key={org.id} style={{ cursor: "pointer" }} onClick={() => nav(`/organizations/${org.id}`)}>
                          <td style={{ fontWeight: 500 }}>{org.name}</td>
                          <td><code className="orgs-slug">{org.slug}</code></td>
                          <td style={{ color: "rgba(120,120,125,1)" }}>{org.description || "—"}</td>
                          <td style={{ color: "rgba(147,147,153,1)" }}>{dateStr}</td>
                          <td><Link to={`/organizations/${org.id}`}><img src="/arrow_right.png" alt="Go" style={{ width: 18, height: 18 }} /></Link></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
}
